import { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Leaf, Sparkles, WifiOff } from "lucide-react";
import { scrollToSection } from "@/hooks/useLenis";

const HERO_IMG =
  "https://images.unsplash.com/photo-1530563937443-1f02f662fa5c?crop=entropy&cs=srgb&fm=jpg&q=85";

const LEAVES = [
  { left: "6%", top: "22%", delay: 0, dur: 7, size: 22 },
  { left: "16%", top: "70%", delay: 1.2, dur: 9, size: 15 },
  { left: "47%", top: "14%", delay: 0.6, dur: 8, size: 17 },
  { left: "90%", top: "34%", delay: 1.8, dur: 7.5, size: 19 },
  { left: "70%", top: "78%", delay: 0.3, dur: 10, size: 13 },
];

const ease = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const rx = useSpring(0, { stiffness: 70, damping: 15 });
  const ry = useSpring(0, { stiffness: 70, damping: 15 });

  const tilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 9);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 9);
  };
  const untilt = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section
      id="home"
      ref={ref}
      className="relative flex min-h-screen items-center overflow-hidden pb-20 pt-28 md:pt-32"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="" className="h-[115%] w-full object-cover opacity-[0.14]" />
        <div className="absolute inset-0 bg-gradient-to-b from-forest-950/60 via-forest-950/80 to-forest-950" />
      </motion.div>

      {LEAVES.map((l, i) => (
        <motion.div
          key={i}
          className="absolute text-moss/25"
          style={{ left: l.left, top: l.top }}
          animate={{ y: [0, -18, 0], rotate: [0, 12, -6, 0] }}
          transition={{ duration: l.dur, delay: l.delay, repeat: Infinity, ease: "easeInOut" }}
        >
          <Leaf style={{ width: l.size, height: l.size }} />
        </motion.div>
      ))}

      <motion.div
        style={{ opacity: fade }}
        className="mx-auto grid w-full max-w-7xl items-center gap-14 px-5 md:px-12 lg:grid-cols-[1.12fr_0.88fr] lg:gap-12"
      >
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease }}
            className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-moss sm:text-xs"
          >
            <span className="h-px w-8 bg-moss/60" />
            Hacktoberfest · EcoRoute Edition
          </motion.p>

          <h1 className="mt-6 font-display text-[clamp(2.7rem,7.2vw,5.4rem)] leading-[1.04] tracking-tight text-cream">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%", rotate: 3 }}
                animate={{ y: 0, rotate: 0 }}
                transition={{ duration: 1, delay: 0.18, ease }}
              >
                Escape Your Screen.
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block bg-gradient-to-r from-moss via-[#8fd3a8] to-clay bg-clip-text italic text-transparent"
                initial={{ y: "112%", rotate: 3 }}
                animate={{ y: 0, rotate: 0 }}
                transition={{ duration: 1, delay: 0.34, ease }}
              >
                Explore Nature.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease }}
            className="mt-6 max-w-xl text-base leading-relaxed text-forest-dim md:text-lg"
          >
            AI-curated eco-routes matched to your mood — shady forests, open meadows, lakeside
            light and autumn fire. Built at Hacktoberfest to get you off the scroll and onto the
            soil.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToSection("#routes")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-moss px-7 py-3.5 font-semibold text-forest-950 transition-all hover:-translate-y-0.5 hover:shadow-[0_0_35px_rgba(82,183,136,0.35)] active:translate-y-0"
            >
              Find Your Route
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollToSection("#about")}
              className="inline-flex items-center gap-2 rounded-full border border-forest-500/60 px-7 py-3.5 text-cream/90 transition-colors hover:border-moss/70 hover:text-moss"
            >
              <Sparkles className="h-4 w-4" />
              The Bonus Challenge
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-forest-muted"
          >
            No accounts · No ads · Just leaves
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease }}
          className="hidden sm:block"
          style={{ perspective: 1100 }}
        >
          <motion.div
            onMouseMove={tilt}
            onMouseLeave={untilt}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="arch-frame relative mx-auto max-w-[380px] border border-forest-500/40 bg-forest-800/40 p-3 shadow-[0_40px_80px_rgba(0,0,0,0.45)]"
          >
            <div className="arch-frame overflow-hidden">
              <motion.img
                src={HERO_IMG}
                alt="Sunlit forest trail"
                style={{ y: imgY, scale: 1.16 }}
                className="h-[440px] w-full object-cover"
              />
            </div>
            <div
              style={{ transform: "translateZ(45px)" }}
              className="absolute -left-8 top-16 flex items-center gap-2 rounded-full border border-forest-500/50 bg-forest-950/90 px-4 py-2 text-xs text-forest-dim backdrop-blur"
            >
              <Leaf className="h-3.5 w-3.5 text-moss" /> Nature-first
            </div>
            <div
              style={{ transform: "translateZ(60px)" }}
              className="absolute -right-6 bottom-24 flex items-center gap-2 rounded-full border border-forest-500/50 bg-forest-950/90 px-4 py-2 text-xs text-forest-dim backdrop-blur"
            >
              <WifiOff className="h-3.5 w-3.5 text-clay" /> Works offline
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}
